import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { BehaviorSubject, combineLatest, firstValueFrom, Observable, Subject, throwError } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { ToastMessageType, ToastService } from '../../../../Common/toast/';
import { FormFlowFieldTypes } from '../../../../Constants/FormFlowFieldTypes';
import { Tenant } from '../../../../Models/tenant';
import { TenantService } from '../../../../Services/tenant.service';
import { TreeNode } from '../../Airsweb/Models/tree-node';
import { DefaultDataValue, Field, FieldGroup, FieldOrder, IChanges, IStateNode, Module, ModuleContent, ModuleDraft, ModuleCard, Step, ValueIsProperty } from '../Models';
import { IPrimaryFields } from '../Models/FormFlowViewModel/IPrimaryFields';
import { Patch } from '../Models/Patch';
import { CompareFormFlowStateVersions } from "@app/Tenant/Applications/Airsweb/Models/compare-formflow-state-versions";
import { ModuleWithAllVersions } from "@app/Tenant/Applications/Airsweb/Models/modules-with-all-versions.model";
import { SortComparisons } from '@app/Common/sorting';
import { PackageService } from './package.service';
import { PackageCreationRules } from '../Models/package-creation-rules';

@Injectable({
    providedIn: 'root'
})
export class ModuleService {
    moduleContent: ModuleContent;
    moduleStateContent: IStateNode[];
    selectedStep: Step = new Step();
    fieldGroups: FieldGroup[] = [];
    fields: Field[] = [];
    fieldsPath: string[] = [];
    fieldChanges$: Subject<Field[]> = new BehaviorSubject(null);
    fields$: Subject<Field[]> = new BehaviorSubject(null);
    hasPrimaryFields$: Subject<boolean> = new BehaviorSubject(null);

    private readonly editList$: Subject<{ listAlias: string; fieldName: string }> = new Subject();
    private readonly addList$: Subject<void> = new Subject();

    private readonly latestModuleContent: BehaviorSubject<ModuleContent> = new BehaviorSubject(null);
    private readonly apiUrl = '/api/module/';

    constructor(private readonly http: HttpClient,
        private readonly toast: ToastService,
        private readonly tenantService: TenantService,
        private readonly packageService: PackageService,
        public dialog: MatDialog) {
    }

    async updateSubmissionTree(tenant: Tenant, moduleId: string, version: number, tree: TreeNode[]) {
        return await this.http.post(`${this.apiUrl}submissiontreeupdate/${tenant.TenantId}/${moduleId}/${version}`, tree)
            .toPromise();
    }

    getSubmissionTree(tenant: Tenant, moduleId: string, version: number): Promise<TreeNode[]> {
        return this.http.get<TreeNode[]>(`${this.apiUrl}submissiontree/${tenant.TenantId}/${moduleId}/${version}`)
            .toPromise()
            .then((data) => data as TreeNode[]);
    }

    getEditList(): Observable<{ listAlias: string; fieldName: string }> {
        return this.editList$.asObservable();
    }

    getAddList(): Observable<any> {
        return this.addList$.asObservable();
    }

    addList() {
        return this.addList$.next();
    }

    editList(listAlias: string, fieldName: string) {
        return this.editList$.next({ listAlias, fieldName });
    }

    private readonly disallowedFieldTypes: string[] = [
        FormFlowFieldTypes.MultiItem,
        FormFlowFieldTypes.Textbox,
        FormFlowFieldTypes.Date,
        FormFlowFieldTypes.DateTime
    ];

    private readonly allowedFieldTypes: string[] = Object.values(FormFlowFieldTypes).filter(fieldType => !this.disallowedFieldTypes.includes(fieldType));

    getAllowedFieldTypes(): Promise<string[]> {
        return new Promise<string[]>((resolve) => {
            resolve(this.allowedFieldTypes);
        });
    }

    public async getCurrentModuleVersion(tenant: Tenant, moduleId: string): Promise<number> {
        const latestModule = await this.http.get<Module>(`${this.apiUrl}get/${tenant.TenantId}/${moduleId}/latestversion`).toPromise();
        return latestModule?.MajorBaseVersion ?? 1;
    }

    public getCurrentModuleVersions(tenant: Tenant): Observable<Map<string, number>> {
        const subject = new Subject<Map<string, number>>();
        combineLatest([
            this.tenantService.getAllTenantModules(tenant.Value),
            this.getAllModulesWithVersions(tenant)
        ]).subscribe(([availableModules, moduleVersions]) => {
            subject.next(new Map(availableModules.map(module => {
                const moduleId = module.UniqueId;
                const latestModuleVersion = Math.max(...(moduleVersions.find(moduleVersion => moduleVersion.ModuleId.toUpperCase() === moduleId.toUpperCase())?.Versions ?? [1]));
                return [moduleId, latestModuleVersion] as const;
            })));
            subject.complete();
        });
        return subject.asObservable();
    }

    public pushStateUpdatesOntoObject(version: number, module: string, tenant: Tenant, object: IChanges): Promise<IChanges> {
        if (version === undefined) {
            object.Loading = false;
            object.HasChanges = 'N/A';
            object.HasError = true;
            return Promise.resolve(object);
        }

        object.Loading = true;
        return this.packageService.getPackageCreationRules(tenant, module)
            .then((result: PackageCreationRules) => {
                object.Loading = false;
                object.HasError = false;

                object.IsPartiallySynced = (result.MajorPackage.CanCreatePackageForThisVersion && !result.MajorPackage.OutOfSync) || (result.MinorPackage.CanCreatePackageForThisVersion && !result.MinorPackage.OutOfSync);
                object.IsFullySynced = (!result.MajorPackage.CanCreatePackageForThisVersion || !result.MajorPackage.OutOfSync) && (!result.MinorPackage.CanCreatePackageForThisVersion || !result.MinorPackage.OutOfSync);

                if (result.ChangeDetectionAvailable) {
                    object.Changes = (result.MajorPackage.CanCreatePackageForThisVersion && result.MajorPackage.HasChanges) || (result.MinorPackage.CanCreatePackageForThisVersion && result.MinorPackage.HasChanges);
                    object.HasChanges = object.Changes ? 'Yes' : 'No';
                }
                else {
                    object.Changes = false;
                    object.HasChanges = 'N/A';
                }

                object.LatestServerVersion = result.LatestServerVersion;
                object.IsLatestServerVersion = version === result.LatestServerVersion

                return object;
            },
            () => {
                object.Loading = false;
                object.HasChanges = 'N/A';
                object.HasError = true;
                return object;
            });
    }

    public async getModules(tenant: Tenant): Promise<Module[]> {
        const [availableModules, allowedModules, moduleDrafts] = await firstValueFrom(combineLatest([
            this.tenantService.getAllTenantModules(tenant.Value),
            this.tenantService.getAllowedModules(tenant.Value),
            this.http.get<ModuleDraft[]>(`${this.apiUrl}get/${tenant.TenantId}/drafts`)
        ]));

        const allowedModulesUpper = allowedModules.map(id => id.toUpperCase());
        const modules = availableModules.map(module => this.buildModuleCard(module, allowedModulesUpper, moduleDrafts));

        modules.sort((a, b) =>
            SortComparisons.compareBoolean(a.IsEnabled, b.IsEnabled, true)
            || SortComparisons.compareBoolean(a.IsEditable, b.IsEditable, true)
            || SortComparisons.compareText(a.ModuleName, b.ModuleName)
            || SortComparisons.compareText(a.ModuleId, b.ModuleId)
        );

        return modules;
    }

    private buildModuleCard(module: { UniqueId: string; Name: string; Description: string }, allowedModulesUpper: string[], moduleDrafts: ModuleDraft[]): Module {
        const moduleIdUpper = module.UniqueId.toUpperCase();
        const draft = moduleDrafts.find(d => d.ModuleId.toUpperCase() === moduleIdUpper);
        const isEnabled = allowedModulesUpper.includes(moduleIdUpper);

        return new Module({
            ModuleId: module.UniqueId,
            ModuleName: module.Name,
            ModuleDescription: module.Description,
            MajorBaseVersion: draft?.MajorBaseVersion,
            MinorBaseVersion: draft?.MinorBaseVersion,
            IsEnabled: isEnabled,
            IsEditable: isEnabled && draft !== undefined
        });
    }

    public async getModuleCards(tenant: Tenant): Promise<ModuleCard[]> {
        const modules = await this.getModules(tenant);
        const moduleCards = modules.map(module => new ModuleCard(module));

        await Promise.all(moduleCards.map((module: ModuleCard) => {
            return this.pushStateUpdatesOntoObject(module.MajorBaseVersion, module.ModuleId, tenant, module);
        }));

        return moduleCards;
    }

    public module(): Observable<ModuleContent> {
        return this.latestModuleContent.asObservable();
    }

    async loadModuleContent(tenant: Tenant, moduleId: string, version: number): Promise<ModuleContent> {
        try {
            const response = await this.http.get<ModuleContent>(`${this.apiUrl}get/${tenant.TenantId}/${moduleId}/${version}`).toPromise();
            this.moduleContent = response;
            this.latestModuleContent.next(this.moduleContent);
            return response;
        } catch (e) {
            await this.handleError(e);
        }
    }

    async loadModuleStateContent(tenant: Tenant, moduleId: string, version: number): Promise<any> {
        try {
            const response = await this.http.get<IStateNode[]>(`${this.apiUrl}get/${tenant.TenantId}/${moduleId}/state/${version}`).toPromise();
            this.moduleStateContent = response as IStateNode[];
        } catch (e) {
            await this.handleError(e);
        }
    }

    public getModuleStates(tenant: Tenant, moduleId: string, version: number): Observable<IStateNode[]> {
        return this.http.get<IStateNode[]>(`${this.apiUrl}get/${tenant.TenantId}/${moduleId}/state/${version}`);
    }

    validateModuleStateContent(): string[] {
        const validationErrors: string[] = [];

        for (const stateNode of this.moduleStateContent) {
            for (const transition of stateNode.StateTransitions) {
                if (!transition.Description || transition.Description.length === 0) { validationErrors.push('Transition Description must not be blank'); }
                if (transition.ModuleStatusId === null || transition.ModuleStatusId === undefined) { validationErrors.push(`In the Transistion with description "${transition.Description}" - the Transition Module Status ID must not be blank`); }
                if (!transition.PostConditionValues.hasOwnProperty('TransitionReason') || !transition.PostConditionValues.TransitionReason || transition.PostConditionValues.TransitionReason.length === 0) { validationErrors.push(`In the Transistion with description "${transition.Description}" - the Transition Reason must not be blank`); }
            }
        }

        return validationErrors;
    }

    updateModuleStateContent(tenant: Tenant, moduleId: string, version: number): Observable<any> {
        return this.http.post(`${this.apiUrl}updatestate/${tenant.TenantId}/${moduleId}/state/${version}`, this.moduleStateContent).pipe(
            map((result) => {
                this.moduleStateContent = [...this.moduleStateContent];
                return result;
            }));
    }

    async resetModuleContent(tenant: Tenant, moduleId: string, version?: number): Promise<any> {
        try {
            let parameters = new HttpParams();
            if (version !== undefined) {
                parameters = parameters.append('version', version);
            }
            await this.http.post(`${this.apiUrl}reset/${tenant.TenantId}/${moduleId}`, null, {
                headers: new HttpHeaders().set('Content-Type', 'application/json'),
                params: parameters
            }).toPromise();

            this.toast.sendMessage('Reset Success', `Module ${moduleId} is up to date`, ToastMessageType.success);
        } catch(err) {
            this.toast.sendMessage('Error Resetting Module', err.statusText, ToastMessageType.error);
            this.handleError(err);
        }
    }

    previewChanges(tenant: Tenant, moduleId: string): Promise<any> {
        return this.http.get(this.apiUrl + 'previewChanges/' + tenant.TenantId + '/' + moduleId, {})
            .toPromise()
            .then((response: Response) => response)
            .catch(this.handleError);
    }

    compareVersions(tenant: Tenant, moduleId: string, versionFrom: number, versionTo: number): Observable<CompareFormFlowStateVersions> {
        return this.http.get<CompareFormFlowStateVersions>(this.apiUrl + 'compareVersions/' + tenant.TenantId + '/' + moduleId + '/' + versionFrom + '/' + versionTo, {});
    }

    getAllModulesWithVersions(tenant: Tenant): Observable<ModuleWithAllVersions[]> {
        return this.http.get<ModuleWithAllVersions[]> (this.apiUrl + 'allmoduleswithversions/' + tenant.TenantId, {});
    }

    getModuleSteps(): Observable<Step[]> {
        return this.latestModuleContent.asObservable().pipe(map(x => x.Steps));
    }

    getModuleFields(): Field[] {
        let moduleFields: Field[] = [];

        this.moduleContent.Steps.forEach((step) => {
            moduleFields = moduleFields.concat(step.Fields.map<Field>((f) => {
                f.MultiFieldName = step.MultiFieldName;
                return f;
            }));
        });

        return moduleFields;
    }

    getModuleFieldsForPreviousSteps(stepId: string): Field[] {
        let moduleFields: Field[] = [];
        let previousSteps: Step[] = [];
        let found = false;

        if (this.moduleContent) {
            this.moduleContent.Steps.forEach((step) => {
                if (step.StepId != stepId && !found) {
                    previousSteps = previousSteps.concat(step);
                }
                if (step.StepId === stepId) {
                    found = true;
                }
            });
        }

        previousSteps.forEach((step) => {
            moduleFields = moduleFields.concat(step.Fields.map<Field>((f) => {
                f.MultiFieldName = step.MultiFieldName;
                return f;
            }));
        });

        return moduleFields;
    }

    getStepFields(stepId: string): Promise<any> {
        return new Promise((resolve, reject) => {
            const self = this;

            self.selectedStep = self.moduleContent.Steps.find(s => s.StepId === stepId);

            self.fieldsPath = self.selectedStep.IsMultiStep
                ? ['Steps', stepId, 'Fields', self.selectedStep.MultiFieldName, 'Control', 'MultiFields']
                : ['Steps', stepId, 'Fields'];

            self.fields = [];
            self.fieldGroups = [];
            self.hasPrimaryFields$.next(self.selectedStep.Fields.some(field => field.HasPrimaryField));
            self.selectedStep.Fields.forEach(f => {
                const fieldGroup: FieldGroup = {
                    GroupName: f.GroupName,
                    UiGroupName: f.UiGroupName
                };
                if (!self.fieldGroups.some(fg => fg.UiGroupName === fieldGroup.UiGroupName)) {
                    self.fieldGroups.push(fieldGroup);
                }
                self.fields.push(f);
            });
            this.fields$.next(this.fields);
            resolve(this.fields);
        });
    }

    getStepMultiFields(stepId: string): Promise<any> {
        return new Promise((resolve, reject) => {
            const self = this;

            self.selectedStep = self.moduleContent.Steps.find(s => s.StepId === stepId);

            self.fields = [];
            self.fieldGroups = [];

            const multiFields = self.selectedStep.Fields.filter(f => f.IsMulti);
            if (multiFields.length !== 1) { throw 'Multiple multi fields are on this step, don\'t know which to pick'; }

            const multiField = multiFields[0];

            self.fieldsPath = self.selectedStep.IsMultiStep
                ? ['Steps', stepId, 'Fields', self.selectedStep.MultiFieldName, 'Control', 'MultiFields', multiField.FieldId, 'Control', 'MultiFields']
                : ['Steps', stepId, 'Fields', multiField.FieldId, 'Control', 'MultiFields'];

            multiField.MultiFields.forEach(m => {
                const fieldGroup: FieldGroup = {
                    GroupName: m.GroupName,
                    UiGroupName: m.UiGroupName
                };
                if (!self.fieldGroups.some(fg => fg.UiGroupName === fieldGroup.UiGroupName)) {
                    self.fieldGroups.push(fieldGroup);
                }
                self.fields.push(m);
            });
            this.fields$.next(this.fields);
            resolve(this.fields);
        });
    }

    public notifyFieldUpdate(): void {
        this.fieldChanges$.next(this.selectedStep.Fields);
    }

    public getFieldsUpdated(): Observable<Field[]> {
        return this.fieldChanges$.asObservable();
    }

    getCurrentStepFields(): Observable<Field[]> {
        return this.fields$.asObservable();
    }

    getStepHasPrimaryFields(): Observable<boolean> {
        return this.hasPrimaryFields$.asObservable();
    }

    getField(stepId: string, parentPropertyName: string): Field {
        const step = this.moduleContent.Steps.find(s => s.StepId === stepId);
        const field = step.Fields.find(f => f.FieldId === parentPropertyName || f.MultiFields.find(mf => mf.FieldId === parentPropertyName));
        return field.MultiFields.length > 0 ? field.MultiFields.find(mf => mf.FieldId === parentPropertyName) : field;
    }

    getDefaultDataValues(stepId: string, onChangePropertyName: string): DefaultDataValue[] {
        if (!onChangePropertyName || !stepId) { return; }

        const step = this.moduleContent.Steps.find(s => s.StepId === stepId);
        if (!step || !step.DefaultDataValues) { return; }

        return step.DefaultDataValues.filter(ddv => ddv.OnChangePropertyName === onChangePropertyName);
    }

    addField(newField: Field, tenant: Tenant, moduleId: string, version: number, stepId: string): Observable<Field> {
        return this.http
            .post(this.apiUrl + 'add/' + tenant.TenantId + '/' + moduleId + '/' + version + '/' + stepId, newField).pipe(map(
                data => {
                    this.selectedStep.Fields.push(newField);
                    this.notifyFieldUpdate();
                    return newField;
                }, (error: Response) => {
                    console.log(error);
                }
            ));
    }

    updateField(field: Field, tenant: Tenant, moduleId: string, version: number, stepId: string): Observable<Field> {
        return this.http
            .post(this.apiUrl + 'update/' + tenant.TenantId + '/' + moduleId + '/' + version + '/' + stepId, field).pipe(map(data => {
                this.fields[field.index] = field;
                this.notifyFieldUpdate();
                return field;
            }, (error: Response) => {
                console.log(error);
            }
            ));
    }

    public addGroup(newFieldGroup: FieldGroup): void {
        this.fieldGroups.push(newFieldGroup);
    }

    updateFields(fields: Field[], tenant: Tenant, moduleId: string, version: number, stepId: string): Observable<Field[]> {
        return this.http
            .post(this.apiUrl + 'update/fields/' + tenant.TenantId + '/' + moduleId + '/' + version + '/' + stepId, fields).pipe(map(
                data => {
                    fields.forEach(field => {
                        this.fields[field.index] = field;
                    });
                    this.notifyFieldUpdate();
                    return fields;
                }, (error: Response) => {
                    console.log(error);
                }
            ));
    }

    updateFieldBasedAvailability(fieldBasedAvailability: ValueIsProperty[], tenant: Tenant, moduleId: string, version: number, stepId: string, stepName: string): Observable<ValueIsProperty[]> {
        return this.http
            .post(this.apiUrl + 'update/availability/' + tenant.TenantId + '/' + moduleId + '/' + version + '/' + stepId, fieldBasedAvailability).pipe(map(
                data => {
                    this.toast.sendMessage('Saved',
                        'FieldBasedAvailability for step ' + stepName + ' updated',
                        ToastMessageType.success);
                    return fieldBasedAvailability;
                }, (error: Response) => {
                    console.log(error);
                    this.toast.sendMessage(
                        'Error Saving FieldBasedAvailability',
                        error.statusText,
                        ToastMessageType.error);
                }
            ));
    }

    updateDefaultDataValues(defaultDataValues: DefaultDataValue[], tenant: Tenant, moduleId: string, version: number, stepId: string): Observable<DefaultDataValue[]> {
        return this.http
            .post(this.apiUrl + 'update/default-data-values/' + tenant.TenantId + '/' + moduleId + '/' + version + '/' + stepId, defaultDataValues).pipe(map(
                () => {
                    const step = this.moduleContent.Steps.find(s => s.StepId === stepId);
                    if (step) {
                        step.DefaultDataValues = defaultDataValues;
                    }

                    if (this.selectedStep?.StepId === stepId) {
                        this.selectedStep.DefaultDataValues = defaultDataValues;
                    }

                    this.toast.sendMessage('Saved', 'Default values updated', ToastMessageType.success);
                    return defaultDataValues;
                }
            ), catchError((error: Response) => {
                console.log(error);
                this.toast.sendMessage('Error Saving Default Values', error.statusText, ToastMessageType.error);
                return throwError(() => error);
            }));
    }

    applyPatch(tenant: Tenant, moduleId: string, patch: Patch[]) {
        const url = `api/${tenant.TenantId}/module/${moduleId}`;
        return this.http
            .patch<{}>(url, patch)
            .toPromise();
    }

    patchField(tenant: Tenant, moduleId: string, stepId: string, field: Field): Observable<{}> {
        const url = `api/${tenant.TenantId}/module/${moduleId}/${stepId}/field`;
        return this.http.patch<{}>(url, field);
    }

    updateFieldJson(field: Field, tenant: Tenant, moduleId: string, version: number, stepId: string): Observable<Field> {
        return this.http
            .post(
                this.apiUrl +
                `updatejson/${tenant.TenantId}/${moduleId}/${version}/${stepId}`,
                field
            )
            .pipe(
                map(
                    (data) => {
                        this.fields[field.index] = field;
                        this.notifyFieldUpdate();
                        return field;
                    },
                    (error: Response) => {
                        console.log(error);
                    }
                )
            );
    }

    public updateOrder(fieldOrder: FieldOrder, tenant: Tenant, moduleId: string, version: number, stepId: string): Promise<any> {
        return new Promise((res, rej) => {
            this.http
                .post(this.apiUrl + 'updateorder/' + tenant.TenantId + '/' + moduleId + '/' + version + '/' + stepId, fieldOrder)
                .subscribe(
                    data => {
                        res(data);
                    },
                    error => {
                        console.log(error);
                        rej(error);
                    });
        });
    }

    public updateOrders(fields: Field[], tenant: Tenant, moduleId: string, version: number, stepId: string): Promise<any> {
        return new Promise((res, rej) => {
            this.http
                .post(this.apiUrl + 'updateorders/' + tenant.TenantId + '/' + moduleId + '/' + version + '/' + stepId, fields)
                .subscribe(
                    data => {
                        res(data);
                    },
                    error => {
                        console.log(error);
                        rej(error);
                    });
        });
    }

    public deleteField(field: Field, tenant: Tenant, moduleId: string, version: number, stepId: string): Promise<any> {
        return new Promise((res, rej) => {
            this.http
                .post(this.apiUrl + 'delete/' + tenant.TenantId + '/' + moduleId + '/' + version + '/' + stepId, field)
                .subscribe(
                    data => {
                        this.selectedStep.Fields.splice(this.selectedStep.Fields.indexOf(field), 1);
                        this.notifyFieldUpdate();
                        res(data);
                    },
                    error => {
                        console.log(error);
                        rej(error);
                    });
        });
    }

    updatePrimaryFieldRules(tenant: Tenant, moduleId: string, version: number, stepId: string, multiParentPropertyName: string, primaryFields: IPrimaryFields): Promise<any> {
        return new Promise((res, rej) => {
            this.http
                .post<void>(this.apiUrl + 'update/primaryFields/' + tenant.TenantId + '/' + moduleId + '/' + version + '/' + stepId + '/' + multiParentPropertyName, primaryFields)
                .subscribe(
                    data => {
                        res(data);
                    },
                    error => {
                        console.log(error);
                        rej(error);
                    });
        });
    }

    isFieldIdUsed(fieldId: string): boolean {
        for (const step of this.moduleContent.Steps) {
            for (const field of step.Fields) {
                if (field.FieldId.toUpperCase() === fieldId) {
                    return true;
                }
            }
        }
        return false;
    }

    private handleError(error: any): Promise<any> {
        console.error('An error occurred', error); // for demo purposes only
        return Promise.reject(error?.error?.ExceptionMessage || error?.message || error);
    }
}
